import React from 'react';
import { StyleSheet, Text, View, Platform, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';

import { moderateScale, isPhone, localTime, isWeb } from '@helpers';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';

import { scale, width as screenWidth } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';

import Image from '@v2/context/Image/context';
import { SCREEN_PADDING_HORIZONTAL, TILE_CONFIG, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { LevelSubScreen } from '@v2/pages/common/enums';
import Empty from '@components/common/Empty';
import { getImageSize, buildImageData, getItemStyle } from './helpers';

const Item = ({
  isEmpty,
  itemSize = TILE_CONFIG.BASE_WIDTH,
  item,
  index,
  C,
  isSmallMode,
  isLineMode,
  generateImageURL,
  open,
  attachMode,
  attachImageId,
  effectiveWidth,
  isPopupMode,
}) => {
  const style = getItemStyle(isEmpty, itemSize, index, isSmallMode, isWeb);

  const imgSize = getImageSize(itemSize, isLineMode);

  const handleOpen = (levelSubScreen = LevelSubScreen.GENERAL) => {
    if (open && typeof open === 'function') {
      open(item, levelSubScreen);
    }
  };

  const imageData = buildImageData(item, imgSize, () => handleOpen(LevelSubScreen.DOCUMENTS), attachMode);

  if (isLineMode) {
    if (isEmpty) return;

    return (
      <View style={{ width: '100%', alignItems: 'center' }}>
        <View style={{ width: effectiveWidth || screenWidth }}>
          <TouchableElement onPress={() => handleOpen()}>
            {({ boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    height: scale(60),
                    paddingHorizontal: scale(isPopupMode ? 20 : SCREEN_PADDING_HORIZONTAL),
                  },
                ]}
              >
                <View style={{ width: '100%', flexDirection: 'row', flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                  <View style={{ width: scale(TILE_CONFIG.LINE_MODE_WIDTH), height: scale(TILE_CONFIG.LINE_MODE_HEIGHT) }}>
                    <Image {...imageData} />
                  </View>
                  <View style={{ flex: 1, paddingLeft: scale(10) }}>
                    <Text numberOfLines={1}>
                      <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{item.productionOrderNumber} </Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{item.description ? `- ${item.description}` : ''}</Text>
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
    );
  }

  return isEmpty ? (
    <View style={[styles.itemComponent, style]} />
  ) : (
    <View style={[styles.itemComponent, style]}>
      <View testID={isSmallMode ? TEST_IDS.searchScreen.productionOrders.itemTile : undefined} style={[imgSize, { backgroundColor: C('Background/Light/Tile No Picture'), borderRadius: scale(TILE_CONFIG.BORDER_RADIUS) }]}>
        <Image {...imageData} />
      </View>
      {!isSmallMode && (
        <TouchableOpacity testID={TEST_IDS.searchScreen.productionOrders.itemTile} activeOpacity={1} onPress={() => handleOpen()}>
          <View>
            <View style={styles.itemImgMarksWrapper}>
              {item.new == 1 ? (
                <View style={[styles.itemStatusMark, { backgroundColor: C('Background / Light / New flag') }]}>
                  <Text style={[styles.itemStatusMarkText, { color: C('Label / Light / AlwaysDark Primary') }, font.Small]}>Nieuw</Text>
                </View>
              ) : (
                <View />
              )}
            </View>
            <View style={styles.itemTextBlock}>
              <View style={styles.lineContainer}>
                <Text style={[styles.itemHeader, { color: C('Label / Light / Primary') }, font.Headline]} numberOfLines={1} ellipsizeMode={'middle'}>
                  {item.productionOrderNumber}
                  <Empty />
                </Text>
              </View>
              <Text
                numberOfLines={1}
                ellipsizeMode={'middle'}
                style={[styles.itemDescription, styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]}
              >
                {item.description}
                <Empty />
              </Text>
              <Text
                numberOfLines={1}
                ellipsizeMode={'middle'}
                style={[styles.itemDescription, styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]}
              >
                {item.partDrawingNumber} {i18n.t('Rev')} {item.partDrawingRevision} {i18n.t('Pos')} {item.partDrawingPosition}
                <Empty />
              </Text>
              <Text
                numberOfLines={1}
                ellipsizeMode={'middle'}
                style={[styles.itemDescription, styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]}
              >
                {item.internalDeliveryDate ? `${i18n.t('Due')} ${localTime(item.internalDeliveryDate).format('DD-MMM-YYYY')}` : ''}
                <Empty />
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default ColorWrapper(Item);

const styles = StyleSheet.create({
  lineContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  itemComponent: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemImgMarksWrapper: {
    display: 'flex',
    paddingLeft: 9,
    paddingRight: 9,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
    position: 'relative',
    top: -27,
    flexDirection: 'row',
  },
  itemStatusMark: {
    paddingLeft: 1,
    paddingRight: 1,
    height: 15,
    borderRadius: 2,
    marginTop: Platform.OS === 'android' ? 0 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStatusMarkText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: moderateScale(9.5),
    marginTop: Platform.OS === 'android' ? -1 : 1,
    letterSpacing: moderateScale(0.02),
    textTransform: 'uppercase',
    paddingLeft: 3,
    paddingRight: 3,
  },
  itemDateIcon: {
    height: 13,
    width: 13,
    marginLeft: Platform.OS === 'android' ? 1 : 0,
    marginTop: 1,
  },
  itemDateMark: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 11,
    height: 22,
    paddingLeft: 8,
    paddingRight: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemDate: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: moderateScale(16),
    lineHeight: moderateScale(19),
    letterSpacing: moderateScale(-0.3),
    marginLeft: 4,
  },
  itemTextBlock: {
    paddingLeft: 9,
    paddingTop: 7,
    position: 'relative',
    top: -21,
  },
  itemHeader: {
    display: 'flex',
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: !isPhone ? moderateScale(17) : 18,
    lineHeight: moderateScale(20),
    letterSpacing: moderateScale(0.02),
  },
  itemHeaderSize: {
    marginLeft: 7,
  },
  itemLightText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto' : 'System',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: !isPhone ? moderateScale(15) : 16,
    lineHeight: moderateScale(18),
    letterSpacing: moderateScale(0.01),
  },
  itemDescription: {
    marginTop: 5,
  },
  itemPart: {
    marginTop: 5,
  },
  itemDrawing: {
    marginTop: 5,
  },
});
